'use client';

import { useState } from 'react';

import { Check, Copy, Maximize2 } from 'lucide-react';

import { Segmented } from '../../components/ui/segmented';
import type { WidgetConfigState } from '../fields';
import type { WidgetSchema } from '../types';
import { buildEmbedCode, supportsScriptEmbed, type EmbedFormat } from '../embed/buildEmbedCode';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard';
import { Button, buttonVariants } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { useOrigin } from '../../lib/useOrigin';

const FORMAT_OPTIONS = [
    { value: 'script', label: 'Script' },
    { value: 'iframe', label: 'iFrame' },
];

export function EmbedCodeBlock({ schema, config }: { schema: WidgetSchema; config: WidgetConfigState }) {
    // Default to the simpler, more robust iframe embed (Polymarket-style); the
    // inline pill needs the script loader for in-text auto-resizing.
    const hasScriptEmbed = supportsScriptEmbed(schema);
    const [format, setFormat] = useState<EmbedFormat>(
        hasScriptEmbed && schema.embed.fixed.widgetSize === 'inline' ? 'script' : 'iframe',
    );
    const origin = useOrigin('https://YOUR_WIDGETS_HOST');
    const { copy, copied } = useCopyToClipboard();

    const code = buildEmbedCode(schema, config, format, origin);

    return (
        <section>
            <div className="mb-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <p className="typ-overline-x10 text-text-x30">Embed code</p>
                    {hasScriptEmbed && (
                        <Segmented
                            value={format}
                            onValueChange={(value) => setFormat(value as EmbedFormat)}
                            options={FORMAT_OPTIONS}
                            aria-label="Embed format"
                        />
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {schema.fullscreenUrl ? (
                        <a
                            href={schema.fullscreenUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
                        >
                            <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
                            Open fullscreen
                        </a>
                    ) : null}
                    <Button type="button" size="sm" onClick={() => copy(code)} className="gap-1.5">
                        {copied ? (
                            <Check className="h-3.5 w-3.5" aria-hidden="true" />
                        ) : (
                            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                        {copied ? 'Copied' : 'Copy code'}
                    </Button>
                </div>
            </div>
            <pre className="max-h-72 overflow-auto rounded-x40 border border-stroke-x30 bg-surface-foregroundOpaque p-5 font-mono text-xs leading-relaxed text-text-x10">
                <code>{code}</code>
            </pre>
        </section>
    );
}
